<script lang="ts">
  import { layout, setView } from '../state/layout.svelte'
  import { location } from '../state/location.svelte'
  import { prefs, setSidebarOpen } from '../state/prefs.svelte'
  import ActiveLayerChip from './ActiveLayerChip.svelte'
  import ForecastSection from './ForecastSection.svelte'
  import Icon from './Icon.svelte'
  import LayerMenu from './LayerMenu.svelte'
  import LocateButton from './LocateButton.svelte'
  import MapView from './MapView.svelte'
  import PlacesSection from './PlacesSection.svelte'
  import Rail from './Rail.svelte'
  import SaveStar from './SaveStar.svelte'
  import SearchForm from './SearchForm.svelte'
  import SectionPanel from './SectionPanel.svelte'
  import SettingsScreen from './SettingsScreen.svelte'
  import MeteogramPanel from './panels/MeteogramPanel.svelte'
  import ModelStatusPanel from './panels/ModelStatusPanel.svelte'
  import SkillPanel from './panels/SkillPanel.svelte'

  /**
   * The desktop (Phase 11 plan §7): a rail on the left, the section docked
   * beside it as a panel, and the map filling the window under both, with
   * its chrome floating over the part the panel leaves open — the search and
   * the active-layer chip along the top, our own zoom, layers and My location
   * down the right, the layer panel to the left of those, and the timeline
   * along the bottom. All of it is placed from one number, where the open map
   * starts (`--chrome-left`), which also pads the camera so a flight centres
   * on the map that shows.
   */

  let { cartoKey }: { cartoKey: string } = $props()

  const TITLES = { forecast: 'Forecast', models: 'Models', places: 'Places', settings: 'Settings' }

  /** 12 px, the 76 px rail, 12 px: where the panel starts. */
  const RAIL_RIGHT = 100
  const GAP = 12

  let map: MapView | undefined = $state()
  let layersOpen = $state(false)
  let panelWidth = $state(0)

  let open = $derived(prefs.sidebarOpen)
  let title = $derived(TITLES[layout.view as keyof typeof TITLES] ?? 'Forecast')
  let chromeLeft = $derived(open && panelWidth > 0 ? RAIL_RIGHT + panelWidth + GAP : RAIL_RIGHT)

  // A section asked for by something other than the rail ("Forecast here",
  // a notification, the star's Settings row) brings a folded panel back.
  let lastView = layout.view // Not $state: only this effect reads it.
  $effect(() => {
    const view = layout.view
    if (view === lastView) return
    lastView = view
    if (!prefs.sidebarOpen) setSidebarOpen(true)
  })

  // Searching from Places or Settings means "show me that place"; from
  // Forecast or Models the section on screen already follows the point.
  function searched() {
    if (layout.view === 'places' || layout.view === 'settings') setView('forecast')
  }
</script>

<main class="desktop" style:--chrome-left="{chromeLeft}px">
  <div class="map-area">
    <MapView
      bind:this={map}
      {cartoKey}
      padding={{ top: 72, bottom: 140, left: chromeLeft, right: 72 }}
    />
  </div>

  <div class="rail-slot"><Rail /></div>

  {#if open}
    <div class="panel-slot" id="section-panel" bind:offsetWidth={panelWidth}>
      <SectionPanel {title} docked>
        {#snippet actions()}
          {#if layout.view !== 'settings'}
            <LocateButton ink />
          {/if}
          {#if location.selected && layout.view !== 'settings'}
            <SaveStar size={18} />
          {/if}
        {/snippet}

        {#if layout.view === 'models'}
          <!-- The point's whole time axis from every model, how they have
               been doing here, and whether they are arriving. Each still
               opens on a click and fetches nothing until then. -->
          <MeteogramPanel />
          <SkillPanel />
          <ModelStatusPanel />
        {:else if layout.view === 'places'}
          <PlacesSection onPicked={() => setView('forecast')} onManage={() => setView('settings')} />
        {:else if layout.view === 'settings'}
          <SettingsScreen {cartoKey} />
        {:else}
          <ForecastSection />
        {/if}
      </SectionPanel>
    </div>
  {/if}

  <!-- Over the open map only, so the layer panel's scrim never swallows a
       click meant for the panel. Pointer events pass through the box. -->
  <div class="chrome">
    <div class="top-row">
      <div class="search"><SearchForm variant="float" onResolved={searched} /></div>
      <div class="chip"><ActiveLayerChip onOpen={() => (layersOpen = true)} /></div>
    </div>

    <div class="controls">
      <div class="zoom">
        <button type="button" class="wx-float-btn touch-target" aria-label="Zoom in" title="Zoom in" onclick={() => map?.zoomIn()}>
          <Icon name="plus" size={20} />
        </button>
        <button type="button" class="wx-float-btn touch-target" aria-label="Zoom out" title="Zoom out" onclick={() => map?.zoomOut()}>
          <Icon name="minus" size={20} />
        </button>
      </div>
      <button
        type="button"
        class="wx-float-btn touch-target"
        aria-label="Map layers"
        title="Layers"
        aria-expanded={layersOpen}
        onclick={() => (layersOpen = !layersOpen)}
      >
        <Icon name="layers" size={20} />
      </button>
      <LocateButton />
    </div>

    <LayerMenu bind:open={layersOpen} fab={false} />
  </div>
</main>

<style>
  .desktop {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
    /* Under the search row: where the PWA toast and a popup may start. */
    --controls-bottom: 60px;
  }

  .map-area {
    position: absolute;
    inset: 0;
  }

  /* MapLibre's scale bar sits on the open map, not under the rail. */
  .desktop :global(.maplibregl-ctrl-bottom-left) {
    left: calc(var(--chrome-left) - 10px);
  }

  .rail-slot {
    position: absolute;
    z-index: 22;
    left: 12px;
    top: 12px;
    bottom: 12px;
  }

  /* 432 px from 1440 px up, a third of a 1280 px laptop (384), and never
     under 360: the README's second open question, answered in R.4. */
  .panel-slot {
    position: absolute;
    z-index: 21;
    left: 100px;
    top: 12px;
    bottom: 12px;
    width: clamp(360px, 30vw, 432px);
    display: flex;
    flex-direction: column;
  }

  .chrome {
    position: absolute;
    z-index: 18;
    top: 0;
    bottom: 0;
    left: calc(var(--chrome-left) - 12px);
    right: 0;
    pointer-events: none;
  }

  .chrome > :global(*) {
    pointer-events: auto;
  }

  .top-row,
  .controls {
    pointer-events: none;
  }

  .top-row > *,
  .controls > :global(*) {
    pointer-events: auto;
  }

  .top-row {
    position: absolute;
    top: 12px;
    left: 12px;
    right: 72px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .search {
    flex: 0 1 380px;
    min-width: 220px;
  }

  .chip {
    flex: 0 1 auto;
    min-width: 0;
    --active-chip-h: 48px;
    --active-chip-px: 16px;
    --active-chip-fs: 15px;
  }

  .controls {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    gap: 10px;
    justify-items: center;
  }

  /* Two buttons on one shadow, split by a hairline (the redesign's `.zoom`). */
  .zoom {
    display: grid;
    box-shadow: var(--float-shadow);
  }

  .zoom > :global(button) {
    box-shadow: none;
  }

  .zoom > :global(button + button) {
    border-top: 1px solid var(--color-divider);
  }
</style>
